import { ReactNode, useEffect, useRef } from 'react';
import gsap from 'gsap';
import './GsapMarquee.css';

type Props = {
  children: ReactNode;
  speed?: number;
  reverse?: boolean;
};

/**
 * GSAP-driven horizontal marquee — uses gsap.fromTo with xPercent for buttery
 * GPU-accelerated infinite scroll that pauses on hover.
 */
export function GsapMarquee({ children, speed = 30, reverse = false }: Props) {
  const trackRef = useRef<HTMLDivElement>(null);
  const tweenRef = useRef<gsap.core.Tween | null>(null);

  useEffect(() => {
    if (!trackRef.current) return;
    const ctx = gsap.context(() => {
      tweenRef.current = gsap.fromTo(
        trackRef.current,
        { xPercent: 0 },
        {
          xPercent: reverse ? 33.3333 : -33.3333,
          duration: speed,
          repeat: -1,
          ease: 'none',
        }
      );
    });
    return () => ctx.revert();
  }, [speed, reverse]);

  const pause = () => tweenRef.current?.timeScale(0.15);
  const resume = () => tweenRef.current?.timeScale(1);

  return (
    <div className="gmarquee" onMouseEnter={pause} onMouseLeave={resume}>
      <div ref={trackRef} className="gmarquee__track">
        <div className="gmarquee__group">{children}</div>
        <div className="gmarquee__group" aria-hidden>{children}</div>
        <div className="gmarquee__group" aria-hidden>{children}</div>
      </div>
    </div>
  );
}
